import type { ChatMessageDto } from '@/lib/api/sessions.client';
import type { IChatAttachment, IMessage, IMessageSource, IToolRun } from '@/lib/types/chat';
import { messageModelOf } from '@/lib/stores/chat-message-model';
import { subAgentNoticeOf } from '@/lib/stores/chat-message-notice';
import { stopNoticesOf } from '@/lib/stores/chat-message-stops';
import { messagePartsOf } from '@/lib/stores/chat-message-parts';
import { contextUsageOf } from '@/lib/stores/message-context-usage';

/**
 * Stored messages as the chat shows them (KAN-1266).
 *
 * A stored message names most fields in more than one place: camelCase at the
 * top level (what the API serializes), the snake_case twin (older or in-flight
 * DTOs), and for some the metadata. Each reader below states its order; the
 * precedence is pinned field by field in ``message-dto.test.ts``.
 */

type Loose = Record<string, unknown>;

/** The first of ``values`` that is a string, in the order given. */
const firstString = (...values: unknown[]): string | undefined =>
  values.find((value): value is string => typeof value === 'string');

/** The first of ``values`` that is a number, in the order given. */
const firstNumber = (...values: unknown[]): number | undefined =>
  values.find((value): value is number => typeof value === 'number');

/** ``value[key]`` for whatever ``value`` is; nothing for null or undefined. */
const fieldOf = (value: unknown, key: string): unknown =>
  (value as Loose | null | undefined)?.[key];

/** ``value`` trimmed, if it is a string with something in it. */
const nonBlank = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

const objectsIn = (value: unknown): Loose[] =>
  Array.isArray(value)
    ? value.filter((item): item is Loose => typeof item === 'object' && item !== null)
    : [];

const normalizeAttachments = (value: unknown): IChatAttachment[] =>
  objectsIn(value).map((item) => ({
    id: firstString(item.id, item.file_id),
    name: firstString(item.name, item.file_name),
    url: firstString(item.url, item.storage_path),
    contentType: firstString(item.contentType, item.mime_type),
    status: firstString(item.status),
    size: firstNumber(item.size, item.size_bytes),
  }));

const normalizeSources = (value: unknown): IMessageSource[] =>
  objectsIn(value)
    .map((item) => ({
      title: firstString(item.title),
      url: firstString(item.url),
      snippet: firstString(item.snippet),
      toolType: firstString(item.tool_type, item.toolType),
    }))
    .filter((src) => (src.title && src.title.trim()) || (src.url && src.url.trim()));

const normalizeToolRuns = (value: unknown): IToolRun[] =>
  objectsIn(value).map((item) => ({
    tool: firstString(item.tool),
    type: firstString(item.type),
    status: firstString(item.status),
    cached: typeof item.cached === 'boolean' ? item.cached : undefined,
    error: firstString(item.error),
    result_count: firstNumber(item.result_count),
  }));

/**
 * The thread id (assistant-ui's, kept in the metadata) and the parent's. The
 * id falls back to the database id; the parent prefers the API's top-level
 * field over the thread metadata.
 */
const threadIdsOf = (m: ChatMessageDto): Pick<IMessage, 'id' | 'parentId'> => {
  const thread = m.message_metadata?.thread ?? {};
  return {
    id: nonBlank(fieldOf(thread, 'message_id')) ?? m.id,
    parentId:
      nonBlank(m.parentMessageId ?? m.parent_message_id) ??
      nonBlank(fieldOf(thread, 'parent_id')) ??
      null,
  };
};

/** The backend says ``ai`` for an answer; the thread says ``assistant``. */
const roleOf = (m: ChatMessageDto): IMessage['role'] => {
  const sender = (m.sender || '').toLowerCase();
  return sender === 'ai' ? 'assistant' : (sender as IMessage['role']);
};

/** Author of a user message in a shared agent thread. */
const authorOf = (m: ChatMessageDto): Pick<IMessage, 'authorId' | 'authorName'> => ({
  authorId: firstString(m.authorId, m.author_id) ?? null,
  authorName: firstString(m.authorName, m.author_name) ?? null,
});

/**
 * Which model the message stands for and who served it. ``servedBy`` is set
 * by the backend only when LiteLLM routed the request to another deployment
 * than the one the user picked.
 */
const modelFieldsOf = (
  m: ChatMessageDto,
  rawMeta: Loose
): Pick<IMessage, 'modelId' | 'modelName' | 'servedBy' | 'modelRegion' | 'modelLocation'> => ({
  modelId: messageModelOf(m, rawMeta),
  modelName: firstString(m.modelName, m.model_name, rawMeta['model_name']) ?? null,
  servedBy: firstString(m.servedBy, m.served_by, rawMeta['served_by']) ?? null,
  modelRegion: firstString(m.modelRegion, m.model_region) ?? null,
  modelLocation: firstString(m.modelLocation, m.model_location) ?? null,
});

/** How long the answer took, and its generation-speed metrics (computed backend-side). */
const timingFieldsOf = (
  m: ChatMessageDto
): Pick<
  IMessage,
  'responseDurationMs' | 'ttftMs' | 'tokensPerSecond' | 'writingMs' | 'toolsMs' | 'thinkingMs'
> => ({
  responseDurationMs: firstNumber(m.responseDurationMs, m.response_duration_ms) ?? null,
  ttftMs: firstNumber(m.ttftMs) ?? null,
  tokensPerSecond: firstNumber(m.tokensPerSecond) ?? null,
  writingMs: firstNumber(m.writingMs) ?? null,
  toolsMs: firstNumber(m.toolsMs) ?? null,
  thinkingMs: firstNumber(m.thinkingMs) ?? null,
});

/** The vision (VLM) preprocessing breakdown, already included in the totals. */
const visionFieldsOf = (
  rawMeta: Loose
): Pick<IMessage, 'visionModel' | 'visionDurationMs' | 'visionCost'> => ({
  visionModel: firstString(rawMeta['vision_model']) ?? null,
  visionDurationMs: firstNumber(rawMeta['vision_duration_ms']) ?? null,
  visionCost: firstNumber(rawMeta['vision_cost']) ?? null,
});

/** Sources: the metadata, the tool metadata, nested metadata, then the top level. */
const sourcesOf = (m: ChatMessageDto, rawMeta: Loose): IMessageSource[] =>
  normalizeSources(
    rawMeta['sources'] ??
      fieldOf(rawMeta['tool_metadata'], 'sources') ??
      fieldOf(rawMeta['message_metadata'], 'sources') ??
      fieldOf(m, 'sources') ??
      fieldOf(m, 'source_list')
  );

/** Tool runs: the metadata, the tool metadata, then the top level. */
const toolRunsOf = (m: ChatMessageDto, rawMeta: Loose): IToolRun[] =>
  normalizeToolRuns(
    rawMeta['tool_runs'] ??
      fieldOf(rawMeta['tool_metadata'], 'tool_runs') ??
      fieldOf(m, 'tool_runs')
  );

const toMessage = (m: ChatMessageDto): IMessage => {
  const rawMeta = (m.message_metadata ?? {}) as Loose;
  const { id, parentId } = threadIdsOf(m);
  return {
    id,
    backendId: m.id,
    role: roleOf(m),
    content: m.content,
    timestamp: new Date(m.created_at),
    parentId,
    branchIndex: m.branchIndex ?? m.branch_index ?? 0,
    ...authorOf(m),
    myFeedback: m.myFeedback ?? null,
    attachments: normalizeAttachments(m.attachments ?? rawMeta['attachments']),
    system: firstString(m.system, rawMeta['system']) ?? null,
    ...modelFieldsOf(m, rawMeta),
    ...timingFieldsOf(m),
    estimatedCost: firstNumber(rawMeta['estimated_cost']) ?? null,
    ...visionFieldsOf(rawMeta),
    ...contextUsageOf(m),
    isPartial: rawMeta['partial'] === true,
    ...stopNoticesOf(rawMeta),
    // A background sub-agent's result (KAN-1872): shown from its metadata.
    subagentNotice: subAgentNoticeOf(rawMeta),
    sources: sourcesOf(m, rawMeta),
    toolRuns: toolRunsOf(m, rawMeta),
    ...messagePartsOf(m),
  };
};

// Story 23.8 v1.9: a message flagged ``hidden_from_ui`` (the autonomous
// trigger nudge) is needed for the LLM's conversation, but is no real turn.
const hiddenFromUi = (m: ChatMessageDto): boolean => m.message_metadata?.hidden_from_ui === true;

/**
 * The chat's messages from a message list: hidden ones dropped, the rest
 * converted, in reading order. The backend lists newest first; the list is
 * reversed as it comes, not sorted.
 */
export const mapChatMessages = (msgs: ChatMessageDto[]): IMessage[] =>
  msgs
    .filter((m) => !hiddenFromUi(m))
    .map(toMessage)
    .reverse();
